import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { useFirestore } from '../hooks/useFirestore';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiDollarSign, FiCalendar, FiFileText, FiSend, FiCheck, FiClock, FiAlertTriangle, FiDownload, FiEye } = FiIcons;

const Billing = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('current');
  const [selectedAgency, setSelectedAgency] = useState('all');

  const { documents: agencies, updateDocument: updateAgency } = useFirestore('agencies');
  const { documents: articles } = useFirestore('articles');

  const postpaidAgencies = agencies.filter(agency => agency.payment_type === 'postpaid');

  const getBillingData = useMemo(() => {
    return postpaidAgencies.map(agency => {
      const agencyArticles = articles.filter(article => 
        article.agency_id === agency.id && article.status === 'published'
      );

      const totalArticles = agencyArticles.length;
      const totalAmount = totalArticles * (agency.price_per_article || 0);
      
      // Calculate articles since last billing
      const lastBilledDate = agency.last_billed_at ? new Date(agency.last_billed_at) : new Date(0);
      const unbilledArticles = agencyArticles.filter(article => {
        const publishedDate = new Date(article.published_at || article.created_at);
        return publishedDate > lastBilledDate;
      });

      const unbilledAmount = unbilledArticles.length * (agency.price_per_article || 0);

      return {
        ...agency,
        totalArticles,
        totalAmount,
        unbilledArticles: unbilledArticles.length,
        unbilledAmount,
        isOverdue: agency.billing_status === 'overdue',
        articles: agencyArticles
      };
    });
  }, [postpaidAgencies, articles]);

  const handleMarkAsPaid = async (agencyId) => {
    try {
      await updateAgency(agencyId, {
        billing_status: 'paid',
        last_billed_at: new Date()
      });
      toast.success('Agency marked as paid');
    } catch (error) {
      toast.error('Error updating billing status: ' + error.message);
    }
  };

  const handleMarkAsOverdue = async (agencyId) => {
    try {
      await updateAgency(agencyId, {
        billing_status: 'overdue'
      });
      toast.success('Agency marked as overdue');
    } catch (error) {
      toast.error('Error updating billing status: ' + error.message);
    }
  };

  const handleSendInvoice = (agency) => {
    // In a real app, this would send an email or generate a PDF
    toast.success(`Invoice sent to ${agency.name}`);
  };

  const generateInvoiceData = (agency) => {
    const billingData = getBillingData.find(data => data.id === agency.id);
    return {
      agency: agency.name,
      contact: agency.contact_person,
      email: agency.email,
      articles: billingData.unbilledArticles,
      rate: agency.price_per_article,
      total: billingData.unbilledAmount,
      period: selectedPeriod,
      date: new Date().toLocaleDateString()
    };
  };

  const totalUnbilled = getBillingData.reduce((sum, data) => sum + data.unbilledAmount, 0);
  const totalOverdue = getBillingData
    .filter(data => data.isOverdue)
    .reduce((sum, data) => sum + data.unbilledAmount, 0);
  const totalPaid = getBillingData
    .filter(data => data.billing_status === 'paid')
    .reduce((sum, data) => sum + data.totalAmount, 0);

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between"
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Billing & Invoices</h1>
          <p className="text-gray-600">Manage post-payment agency billing and invoices</p>
        </div>
        <div className="flex space-x-2 mt-4 sm:mt-0">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="current">Current Period</option>
            <option value="last_month">Last Month</option>
            <option value="last_quarter">Last Quarter</option>
            <option value="all_time">All Time</option>
          </select>
        </div>
      </motion.div>

      {/* Billing Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">Total Unbilled</p>
              <p className="text-3xl font-bold text-warning-600">{totalUnbilled.toFixed(2)} LEI</p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-warning-500 flex items-center justify-center">
              <SafeIcon icon={FiClock} className="w-6 h-6 text-white" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">Overdue</p>
              <p className="text-3xl font-bold text-danger-600">{totalOverdue.toFixed(2)} LEI</p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-danger-500 flex items-center justify-center">
              <SafeIcon icon={FiAlertTriangle} className="w-6 h-6 text-white" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">Total Paid</p>
              <p className="text-3xl font-bold text-success-600">{totalPaid.toFixed(2)} LEI</p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-success-500 flex items-center justify-center">
              <SafeIcon icon={FiCheck} className="w-6 h-6 text-white" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">Post-Payment Clients</p>
              <p className="text-3xl font-bold text-primary-600">{postpaidAgencies.length}</p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-primary-500 flex items-center justify-center">
              <SafeIcon icon={FiDollarSign} className="w-6 h-6 text-white" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Billing Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
      >
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Client Billing Status</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Agency
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Unbilled Articles
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount Due
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Billed
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {getBillingData.map((data) => (
                <tr key={data.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{data.name}</div>
                      <div className="text-sm text-gray-500">{data.contact_person}</div>
                      <div className="text-sm text-gray-500">{data.email}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{data.unbilledArticles}</div>
                    <div className="text-sm text-gray-500">@ {data.price_per_article} LEI each</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{data.unbilledAmount.toFixed(2)} LEI</div>
                    {data.credit_limit && data.unbilledAmount > data.credit_limit && (
                      <div className="text-xs text-danger-600">Over credit limit</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      data.billing_status === 'paid' 
                        ? 'bg-success-100 text-success-800'
                        : data.billing_status === 'overdue'
                        ? 'bg-danger-100 text-danger-800'
                        : 'bg-warning-100 text-warning-800'
                    }`}>
                      {data.billing_status?.toUpperCase() || 'PENDING'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {data.last_billed_at 
                      ? new Date(data.last_billed_at).toLocaleDateString()
                      : 'Never'
                    }
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      {data.unbilledAmount > 0 && (
                        <button
                          onClick={() => handleSendInvoice(data)}
                          className="text-primary-600 hover:text-primary-900 p-1 rounded"
                          title="Send Invoice"
                        >
                          <SafeIcon icon={FiSend} className="w-4 h-4" />
                        </button>
                      )}
                      
                      <button
                        onClick={() => console.log('View details', generateInvoiceData(data))}
                        className="text-gray-600 hover:text-gray-900 p-1 rounded"
                        title="View Details"
                      >
                        <SafeIcon icon={FiEye} className="w-4 h-4" />
                      </button>
                      
                      {data.billing_status !== 'paid' && (
                        <button
                          onClick={() => handleMarkAsPaid(data.id)}
                          className="text-success-600 hover:text-success-900 p-1 rounded"
                          title="Mark as Paid"
                        >
                          <SafeIcon icon={FiCheck} className="w-4 h-4" />
                        </button>
                      )}
                      
                      {data.billing_status !== 'overdue' && data.unbilledAmount > 0 && (
                        <button
                          onClick={() => handleMarkAsOverdue(data.id)}
                          className="text-danger-600 hover:text-danger-900 p-1 rounded"
                          title="Mark as Overdue"
                        >
                          <SafeIcon icon={FiAlertTriangle} className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>

      {getBillingData.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-12"
        >
          <SafeIcon icon={FiFileText} className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">No post-payment agencies found</p>
          <p className="text-gray-400 mt-2">
            Add agencies with post-payment billing to start tracking invoices
          </p>
        </motion.div>
      )}
    </div>
  );
};

export default Billing;